import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";

import { ApiContext, UserContext } from "../contexts";
import { FloorplanOption, HouseLand } from "../types";
import { getPriceFormatted } from "../util";

const EstimateRow = (props: {
  label: string;
  price?: number;
  promo_price?: number;
  header?: boolean;
  negativePrice?: boolean;
}) => {
  return (
    <div className={`estimate-${props.header ? "header" : "row"}`}>
      <p className={"estimate-label"}>{props.label}</p>
      <div className={"estimate-separator"} />
      {props.promo_price !== undefined ? (
        <p className={"estimate-price"}>
          {getPriceFormatted(props.promo_price)}
        </p>
      ) : (
        <p className={"estimate-price"}>
          {props.negativePrice && "-"}
          {getPriceFormatted(props.price)}
        </p>
      )}
    </div>
  );
};

const EstimateTable = observer(
  (props: { home: HouseLand; isHouseLand?: boolean }) => {
    const apiContext = useContext(ApiContext);
    const userContext = useContext(UserContext);

    useEffect(() => {
      userContext.debugTotals();
    }, []);

    let tableTitle = "Price Inclusive of Standard Costs";
    if (props.isHouseLand) {
      tableTitle = `${props.home.house_design} - ${props.home.facade_title} Facade, ${props.home.facade_finish}`;
    }

    let selectedFinish;
    if (userContext.selectedFacade) {
      selectedFinish = userContext.selectedFacadeFinishOption.get(
        userContext.selectedFacade.id
      );
    }

    let selectedRoofStyle;
    let selectedRoofStylePrice = 0;
    let selectedGutterStyle;
    let selectedWindowStyle;
    if (userContext.selectedExteriorStyle) {
      selectedRoofStyle = userContext.selectedRoofStyle.get(
        userContext.selectedExteriorStyle.id
      );
      selectedGutterStyle = userContext.selectedGutterStyle.get(
        userContext.selectedExteriorStyle.id
      );
      selectedWindowStyle = userContext.selectedWindowStyle.get(
        userContext.selectedExteriorStyle.id
      );

      if (selectedRoofStyle) {
        if (props.home.roof_upgrades[selectedRoofStyle.id] !== undefined) {
          selectedRoofStylePrice =
            props.home.roof_upgrades[selectedRoofStyle.id];
        } else {
          selectedRoofStylePrice = selectedRoofStyle.price;
        }
      }
    }

    let selectedKitchenStyle;
    let selectedFloorStyle;
    let selectedKitchenStylePrice = 0;
    let selectedFloorStylePrice = 0;
    if (userContext.selectedInteriorStyle) {
      selectedKitchenStyle = userContext.selectedKitchenStyle.get(
        userContext.selectedInteriorStyle.id
      );
      selectedFloorStyle = userContext.selectedFlooringStyle.get(
        userContext.selectedInteriorStyle.id
      );
      if (selectedKitchenStyle) {
        if (
          props.home.kitchen_upgrades[selectedKitchenStyle.id] !== undefined
        ) {
          selectedKitchenStylePrice +=
            props.home.kitchen_upgrades[selectedKitchenStyle.id];
        } else {
          selectedKitchenStylePrice += selectedKitchenStyle.price;
        }
      }
      if (selectedFloorStyle) {
        if (props.home.flooring_upgrades[selectedFloorStyle.id] !== undefined) {
          selectedFloorStylePrice +=
            props.home.flooring_upgrades[selectedFloorStyle.id];
        } else {
          selectedFloorStylePrice += selectedFloorStyle.price;
        }
      }
    }

    let selectedFloorplanOptions: FloorplanOption[] = [];
    if (userContext.selectedHome) {
      selectedFloorplanOptions =
        userContext.selectedFloorplanOptions.get(userContext.selectedHome.id) ||
        [];
    }

    return (
      <div className={"pyht-estimate-table"}>
        <div className={"estimate-section"}>
          <EstimateRow header label={tableTitle} price={
            props.isHouseLand ? 
              userContext.subtotalPriceWithoutLandCost - (userContext.subtotalPrice - props.home.price) : 
              props.home.price
          } />
        </div>
        <div className={"estimate-section"}>
          <EstimateRow header label={"Additional Selections"} price={userContext.subtotalPrice - props.home.price} />
        </div>
        {userContext.selectedFacade && (
          <div className={"estimate-section indented"}>
            <div className={"estimate-header"}>Facade</div>
            <div className={"estimate-subsection"}>
              <EstimateRow
                label={userContext.selectedFacade.title}
                price={userContext.selectedFacade.base_price}
              />
              {selectedFinish && (
                <EstimateRow
                  label={selectedFinish.label}
                  price={selectedFinish.included ? 0 : selectedFinish.price - userContext.selectedFacade.base_price}
                />
              )}
            </div>
          </div>
        )}
        {userContext.selectedExteriorStyle && (
          <div className={"estimate-section indented"}>
            <div className={"estimate-header"}>Exterior Style</div>
            <div className={"estimate-subsection"}>
              <EstimateRow
                label={`Exterior Colour - ${userContext.selectedExteriorStyle.title}`}
                price={userContext.selectedExteriorStyle.price}
              />
              {selectedRoofStyle && (
                <EstimateRow
                  label={`Roof Style - ${selectedRoofStyle.title}`}
                  price={selectedRoofStylePrice}
                />
              )}

              {selectedGutterStyle && (
                <EstimateRow
                  label={`Fascia & Gutters - ${selectedGutterStyle.title}`}
                  price={selectedGutterStyle.price}
                />
              )}

              {selectedWindowStyle && (
                <EstimateRow
                  label={`Windows - ${selectedWindowStyle.title}`}
                  price={selectedWindowStyle.price}
                />
              )}
            </div>
          </div>
        )}
        {(userContext.selectedInclusion ||
          userContext.selectedUpgrades.size > 0) && (
          <div className={"estimate-section indented"}>
            <div className={"estimate-header"}>Upgrade Packs</div>
            <div className={"estimate-subsection"}>
              {userContext.selectedInclusion && (
                <EstimateRow
                  label={userContext.selectedInclusion.title}
                  promo_price={userContext.selectedInclusion.promo_price}
                  price={userContext.selectedInclusion.price}
                />
              )}

              {Array.from(userContext.selectedUpgrades).map((item) => {
                return (
                  <EstimateRow
                    key={item[1].id}
                    label={item[1].title}
                    price={item[1].price}
                    promo_price={item[1].promo_price}
                  />
                );
              })}
            </div>
          </div>
        )}
        {userContext.selectedInteriorStyle && (
          <div className={"estimate-section indented"}>
            <div className={"estimate-header"}>Interior Style</div>
            <div className={"estimate-subsection"}>
              <EstimateRow
                label={`Interior Colour Scheme - ${userContext.selectedInteriorStyle.title}`}
                price={userContext.selectedInteriorStyle.price}
              />
              {selectedKitchenStyle && (
                <EstimateRow
                  label={`Cabinetry Colour - ${selectedKitchenStyle.title}`}
                  price={selectedKitchenStylePrice}
                />
              )}
              {selectedFloorStyle && (
                <EstimateRow
                  label={`Main Flooring - ${selectedFloorStyle.title}`}
                  price={selectedFloorStylePrice}
                />
              )}
            </div>
          </div>
        )}
        {selectedFloorplanOptions.length > 0 && (
          <div className={"estimate-section indented"}>
            <div className={"estimate-header"}>Floorplan Options</div>
            <div className={"estimate-subsection"}>
              {selectedFloorplanOptions.map((item) => {
                return (
                  <EstimateRow
                    key={item.id}
                    label={item.label}
                    price={item.price}
                  />
                );
              })}
            </div>
          </div>
        )}

        {userContext.selectedLifestyleOptions.size > 0 && (
          <div className={"estimate-section indented"}>
            <div className={"estimate-header"}>Lifestyle Options</div>
            <div className={"estimate-subsection"}>
              {Array.from(userContext.selectedLifestyleOptions).map((item) => {
                return item[1].map((upgrade) => {
                  return (
                    <EstimateRow
                      key={upgrade.id}
                      label={upgrade.title}
                      price={upgrade.price}
                      promo_price={upgrade.promo_price}
                    />
                  );
                });
              })}
            </div>
          </div>
        )}
        {userContext.selectedTurnKeyInclusion && (
          <div className={"estimate-section indented"}>
            <div className={"estimate-header"}>Turn Key Inclusions</div>
            <div className={"estimate-subsection"}>
              <EstimateRow
                label={userContext.selectedTurnKeyInclusion.title}
                price={userContext.selectedTurnKeyInclusion.price}
                promo_price={userContext.selectedTurnKeyInclusion.promo_price}
              />
            </div>
          </div>
        )}

        {userContext.siteWorksAmount > 0 && (
          <div className={"estimate-section"}>
            <div className={"estimate-header"}>Site Works</div>
            <div className={"estimate-subsection"}>
              <EstimateRow
                label={`${getPriceFormatted(
                  userContext.siteWorksAmount
                )} fixed price in preferred estate`}
                price={userContext.siteWorksAmount}
              />
            </div>
          </div>
        )}
        {(userContext.selectedSpecialOffer || (userContext.selectedPromotion &&  ["dollar_off_home_plus_no_additional_cost","dollar_amount_bonus","dollar_amount_bonus_plus_floorplan_options"].includes(userContext.selectedPromotion.type))) && 
          ((userContext.selectedSpecialOffer && userContext.selectedSpecialOffer.id === 24) || (userContext.selectedPromotion && userContext.selectedPromotion.id === 24)) 
        && (
          <div
            className="estimate-section indented"
          >
            <EstimateRow
              header
              label={"Energy Saver pack"}
              price={0}
            />
            <EstimateRow
              label={"Double Glazed Windows"}
              price={0}
            />
            <EstimateRow
              label={"6.6kW PV Solar"}
              price={0}
            />
            <EstimateRow
              label={"Induction Cooktop"}
              price={0}
            />
            <EstimateRow
              label={"Hot Water System"}
              price={0}
            />
            <EstimateRow
              label={"Insulation"}
              price={0}
            />
            <EstimateRow
              header
              label={"Air Con"}
              price={0}
            />
          </div>
        )}
        <div
          className={`estimate-section ${
            props.isHouseLand ? "padded-bottom" : ""
          }`}
        >
          {!props.isHouseLand && (
            <EstimateRow
              header
              label={
                userContext.siteWorksAmount > 0
                  ? "Total Fixed Price"
                  : "Total Price"
              }
              price={userContext.subtotalPriceWithSiteWorks}
            />
          )}
          {props.isHouseLand && (
            <EstimateRow
              header
              label={
                props.home.pdf_fixed_price
                  ? "Total Fixed House Price"
                  : "Total House Price"
              }
              price={userContext.subtotalPriceWithoutLandCost}
            />
          )}
          {props.isHouseLand && userContext.landCostAmount > 0 && (
            <EstimateRow
              header
              label={"Land Price"}
              price={userContext.landCostAmount}
            />
          )}
          {props.isHouseLand && (
            <EstimateRow
              header
              label={"Total House and Land Package Price"}
              price={userContext.subtotalPrice}
            />
          )}
          {(userContext.selectedSpecialOffer || (userContext.selectedPromotion &&  ["dollar_off_home_plus_no_additional_cost","dollar_amount_bonus","dollar_amount_bonus_plus_floorplan_options"].includes(userContext.selectedPromotion.type))) && (
            <EstimateRow
              header
              negativePrice
              label={"Promotion"}
              price={userContext.specialOfferAmount}
            />
          )}
          {userContext.applyForGrant === true && (
            <EstimateRow
              header
              negativePrice
              label={apiContext.grantSettings.grant_name}
              price={apiContext.grantSettings.grant_amount}
            />
          )}
          <EstimateRow
            header
            label={"Total After Promotions and Grants"}
            price={userContext.totalPriceWithSiteWorks}
          />
          {userContext.selectedSpecialOffer && userContext.selectedSpecialOffer.show_popout && (
            <div className="promo-copy-popout" dangerouslySetInnerHTML={{ __html: userContext.selectedSpecialOffer.estimate_copy }} />
          )}
          {userContext.selectedPromotion && userContext.selectedPromotion.show_popout && (
            <div className="promo-copy-popout" dangerouslySetInnerHTML={{ __html: userContext.selectedPromotion.estimate_copy }} />
          )}
        </div>
        {props.isHouseLand && (
          <div className={"estimate-section footer-text"}>
            <p>
              Note: This {props.home.pdf_fixed_price ? "fixed price" : ""}{" "}
              package is valid for 30 days from creation.
            </p>
          </div>
        )}
      </div>
    );
  }
);

export default EstimateTable;
